import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

export function LandingPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  // Redirect authenticated users to the app
  useEffect(() => {
    if (!loading && user) {
      navigate('/app', { replace: true });
    }
  }, [user, loading, navigate]);

  // Show nothing while checking auth (prevents flash of landing page)
  if (loading) {
    return null;
  }

  return (
    <div className="landing">
      {/* Navigation */}
      <nav className="landing-nav">
        <div className="landing-nav-content">
          <div className="landing-logo">
            <span className="logo-icon">📖</span>
            <span className="logo-text">Read l8r</span>
          </div>
          <div className="landing-nav-links">
            <a href="#features">Features</a>
            <a href="#pricing">Pricing</a>
            <Link to="/download">Extension</Link>
            <Link to="/app" className="nav-cta">Get Started</Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <h1 className="hero-title">
            Save it.<br />
            Read it.<br />
            <span className="hero-highlight">Remember it.</span>
          </h1>
          <p className="hero-subtitle">
            Your reading queue for articles, newsletters, podcasts, and more.
            Read or listen anywhere, and let AI help you retain what matters.
          </p>
          <div className="hero-cta">
            <Link to="/app" className="btn-primary btn-large">
              Start Reading Free
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </Link>
            <a href="#features" className="btn-secondary btn-large">
              See How It Works
            </a>
          </div>
          <p className="hero-note">Free during beta. No credit card required.</p>
        </div>
        <div className="hero-visual">
          <div className="device-frame">
            <div className="device-screen">
              <div className="mock-header">
                <span>Read l8r</span>
                <span>12 articles</span>
              </div>
              <div className="mock-article">
                <div className="mock-title"></div>
                <div className="mock-meta"></div>
                <div className="mock-excerpt"></div>
              </div>
              <div className="mock-article">
                <div className="mock-title"></div>
                <div className="mock-meta"></div>
                <div className="mock-excerpt"></div>
              </div>
              <div className="mock-player">
                <div className="mock-progress"></div>
                <div className="mock-controls">
                  <span>◀◀</span>
                  <span className="play-circle">▶</span>
                  <span>▶▶</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features">
        <div className="section-header">
          <h2>Everything you need to read smarter</h2>
          <p>Built for curious minds who want to read more and forget less.</p>
        </div>

        <div className="features-grid">
          <div className="feature-card feature-ai">
            <div className="feature-icon">🧠</div>
            <h3>Cross-Article AI Synthesis</h3>
            <p>
              Ask questions across your entire library. Connect ideas from different
              articles and discover insights you might have missed.
            </p>
            <div className="feature-demo">
              <div className="chat-bubble user">"What do my saved articles say about productivity?"</div>
              <div className="chat-bubble ai">Based on 5 articles in your library, the key themes are...</div>
            </div>
          </div>

          <div className="feature-card feature-listen">
            <div className="feature-icon">🎧</div>
            <h3>Listen to Anything</h3>
            <p>
              Turn articles, newsletters, PDFs, and YouTube transcripts into
              natural-sounding audio. Perfect for commutes and workouts.
            </p>
            <div className="feature-formats">
              <span className="format-tag">Articles</span>
              <span className="format-tag">PDFs</span>
              <span className="format-tag">YouTube</span>
              <span className="format-tag">Newsletters</span>
            </div>
          </div>

          <div className="feature-card feature-everywhere">
            <div className="feature-icon">🌐</div>
            <h3>Works Everywhere</h3>
            <p>
              Chrome extension to save with one click. Mobile PWA for reading on the go.
              Native apps coming soon to iOS and Android.
            </p>
            <div className="platform-icons">
              <span className="platform" title="Chrome Extension">🧩</span>
              <span className="platform" title="Mobile Web">📱</span>
              <span className="platform" title="Desktop">💻</span>
              <span className="platform coming-soon" title="iOS - Coming Soon">🍎</span>
              <span className="platform coming-soon" title="Android - Coming Soon">🤖</span>
            </div>
          </div>

          <div className="feature-card feature-privacy">
            <div className="feature-icon">🔒</div>
            <h3>Privacy-First</h3>
            <p>
              Your reading data stays yours. No tracking, no selling your data.
              Bring your own API keys for AI features—we never see them.
            </p>
            <ul className="privacy-list">
              <li>End-to-end encryption option</li>
              <li>Self-hostable</li>
              <li>Export your data anytime</li>
            </ul>
          </div>
        </div>

        <div className="features-bonus">
          <h3>Plus everything you'd expect</h3>
          <div className="bonus-grid">
            <div className="bonus-item">
              <span>✨</span> AI Summaries
            </div>
            <div className="bonus-item">
              <span>🏷️</span> Tags & Collections
            </div>
            <div className="bonus-item">
              <span>🌙</span> Dark Mode
            </div>
            <div className="bonus-item">
              <span>📊</span> Reading Stats
            </div>
            <div className="bonus-item">
              <span>🔥</span> Streaks & Goals
            </div>
            <div className="bonus-item">
              <span>📝</span> Highlights & Notes
            </div>
            <div className="bonus-item">
              <span>🔄</span> Cross-device Sync
            </div>
            <div className="bonus-item">
              <span>📴</span> Offline Reading
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="pricing">
        <div className="section-header">
          <h2>Simple, fair pricing</h2>
          <p>Free during beta. We'll keep a generous free tier forever.</p>
        </div>

        <div className="pricing-cards">
          <div className="pricing-card">
            <div className="pricing-badge">Current</div>
            <h3>Beta</h3>
            <div className="pricing-amount">
              <span className="price">$0</span>
              <span className="period">forever (for now)</span>
            </div>
            <ul className="pricing-features">
              <li>Unlimited articles</li>
              <li>Text-to-speech (bring your own API key)</li>
              <li>AI features (bring your own API key)</li>
              <li>Cross-device sync</li>
              <li>All current features</li>
            </ul>
            <Link to="/app" className="btn-primary">Join the Beta</Link>
          </div>

          <div className="pricing-card pricing-future">
            <h3>Pro</h3>
            <div className="pricing-amount">
              <span className="price">~$5</span>
              <span className="period">/month (planned)</span>
            </div>
            <ul className="pricing-features">
              <li>Everything in Beta</li>
              <li>Included AI credits (no API keys needed)</li>
              <li>Premium TTS voices</li>
              <li>Priority support</li>
              <li>Early access to new features</li>
            </ul>
            <button className="btn-secondary" disabled>Coming Soon</button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="final-cta">
        <h2>Start reading smarter today</h2>
        <p>Join thousands of readers who never lose track of what matters.</p>
        <Link to="/app" className="btn-primary btn-large">
          Get Started Free
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M5 12h14M12 5l7 7-7 7"/>
          </svg>
        </Link>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-content">
          <div className="footer-brand">
            <span className="logo-icon">📖</span>
            <span className="logo-text">Read l8r</span>
            <p>Save it. Read it. Remember it.</p>
          </div>
          <div className="footer-links">
            <div className="footer-column">
              <h4>Product</h4>
              <a href="#features">Features</a>
              <a href="#pricing">Pricing</a>
              <Link to="/app">Web App</Link>
            </div>
            <div className="footer-column">
              <h4>Resources</h4>
              <a href="https://github.com/anthropics/claude-code/issues" target="_blank" rel="noopener noreferrer">Support</a>
              <a href="#">Changelog</a>
              <a href="#">API Docs</a>
            </div>
            <div className="footer-column">
              <h4>Legal</h4>
              <a href="#">Privacy</a>
              <a href="#">Terms</a>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; {new Date().getFullYear()} Read l8r. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
